import { Vector } from './Vector'
import * as PIXI from 'pixi.js'

const SPARK_TRAIL_LENGTH = 0.0018;
const SPARK_LINE_WIDTH = 0.00045;

export class SparkParticle {
    position: Vector;
    velocity: Vector;
    lifeRemainingMs: number;
    readonly maxLifeMs: number;

    constructor(position: Vector, velocity: Vector, maxLifeMs: number) {
        this.position = position;
        this.velocity = velocity;
        this.lifeRemainingMs = maxLifeMs;
        this.maxLifeMs = maxLifeMs;
    }

    update(deltaMs: number): void {
        this.position = Vector.add(this.position, Vector.scale(this.velocity, deltaMs));
        this.velocity = Vector.scale(this.velocity, 0.92);
        this.lifeRemainingMs -= deltaMs;
    }

    isDead(): boolean {
        return this.lifeRemainingMs <= 0;
    }

    getLifeRatio(): number {
        return this.lifeRemainingMs / this.maxLifeMs;
    }

    getColor(): number {
        return this.getLifeRatio() > 0.5 ? 0xffcc66 : 0xff8844;
    }

    getSpeed(): number {
        return Vector.magnitude(this.velocity);
    }

    /**
     * Creates a burst of spark particles emanating from a collision surface.
     * Sparks are jittered along the plane normal to create a spray effect.
     */
    static spawnFromCollision(
        contactPosition: Vector,
        trailDirection: Vector,
        planeNormal: Vector,
        count: number,
        minSpeed: number,
        maxSpeed: number,
        maxLifetime: number
    ): SparkParticle[] {
        const sparks: SparkParticle[] = [];

        for (let index = 0; index < count; index++) {
            const normalJitter = 0.1 + Math.random() * 0.4;
            const directionCandidate = Vector.add(
                trailDirection,
                Vector.scale(planeNormal, normalJitter)
            );
            const directionMagnitude = Vector.magnitude(directionCandidate);
            if (directionMagnitude <= 1e-12) {
                continue;
            }
            const sparkDirection = Vector.scale(directionCandidate, 1 / directionMagnitude);

            const speed = minSpeed + Math.random() * (maxSpeed - minSpeed);
            const life = maxLifetime * (0.7 + Math.random() * 0.3);

            sparks.push(new SparkParticle(
                contactPosition,
                Vector.scale(sparkDirection, speed),
                life
            ));
        }

        return sparks;
    }

    draw(graphics: PIXI.Graphics): void {
        const speed = this.getSpeed();
        if (speed <= 1e-12) {
            return;
        }

        const lifeRatio = this.getLifeRatio();
        const sparkColor = this.getColor();
        const sparkDirection = Vector.scale(this.velocity, 1 / speed);
        const trail = Vector.scale(sparkDirection, SPARK_TRAIL_LENGTH * lifeRatio);

        graphics
            .moveTo(this.position.x, this.position.y)
            .lineTo(this.position.x - trail.x, this.position.y - trail.y)
            .stroke({ width: SPARK_LINE_WIDTH, color: sparkColor });
    }
}
